import { useMemo, useState } from "react";
import type { StoryEditorConfig, StoryEditorItem } from "../scenario/types";
import { audio } from "../lib/audio";
import { tx } from "../lib/tx";

type Phase = "edit" | "result";

/**
 * "The Influencer" arc, finale — the "rewind" minigame. The learner is handed
 * the exact same batch of candidate photos + hashtags from the posting scene
 * and toggles each one "keep" / "cut" before the story actually goes out.
 * On submit, a risk score/breakdown explains which kept items were still
 * risky and why (mirrors the existing `cqr-recap`/`reveal` "what would have
 * happened" tone rather than a hard pass/fail).
 */
export function StoryEditor({ config, onDone }: { config: StoryEditorConfig; onDone: () => void }) {
  const allIds = useMemo(
    () => [...config.photos.map((p) => p.id), ...config.hashtags.map((h) => h.id)],
    [config.photos, config.hashtags],
  );
  const [kept, setKept] = useState<Set<string>>(() => new Set(allIds));
  const [phase, setPhase] = useState<Phase>("edit");

  const toggle = (id: string) => {
    audio.click();
    setKept((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const submit = () => {
    const risky = allIds.filter((id) => kept.has(id) && isRisky(id));
    audio.click();
    if (risky.length === 0) audio.chimeGood();
    else audio.chimeBad();
    setPhase("result");
  };

  function isRisky(id: string): boolean {
    return (
      config.photos.find((p) => p.id === id)?.risky === true ||
      config.hashtags.find((h) => h.id === id)?.risky === true
    );
  }

  const itemsById = (id: string): StoryEditorItem | undefined =>
    config.photos.find((p) => p.id === id) ?? config.hashtags.find((h) => h.id === id);

  const totalRisky = allIds.filter(isRisky).length;
  const keptRisky = allIds.filter((id) => kept.has(id) && isRisky(id));
  const cutRisky = allIds.filter((id) => !kept.has(id) && isRisky(id));
  const score = totalRisky === 0 ? 100 : Math.round((cutRisky.length / totalRisky) * 100);

  if (phase === "result") {
    return (
      <div className="pstory-wrap seditor-wrap" role="dialog" aria-label={tx("Story review")}>
        <div className="pstory-card seditor-card">
          <div className="pstory-head">
            <span className="pstory-head-dot" aria-hidden />
            {config.resultTitle ?? tx("Your edited story")}
          </div>

          <div className={`seditor-score ${score === 100 ? "safe" : score >= 50 ? "warn" : "bad"}`}>
            <div className="seditor-score-num">{score}</div>
            <div className="seditor-score-label">{tx("/ 100 — oversharing risk avoided")}</div>
          </div>

          <div className="seditor-breakdown">
            {keptRisky.length > 0 && (
              <div className="seditor-breakdown-group seditor-breakdown-group--bad">
                <div className="seditor-breakdown-h">{tx("Still risky — you kept these")}</div>
                {keptRisky.map((id) => {
                  const it = itemsById(id);
                  if (!it) return null;
                  return (
                    <div key={id} className="seditor-breakdown-row">
                      {it.url && <img src={it.url} alt={it.alt ?? ""} className="seditor-breakdown-thumb" />}
                      <span className="seditor-breakdown-text">{it.label ?? it.riskNote}</span>
                    </div>
                  );
                })}
              </div>
            )}
            {cutRisky.length > 0 && (
              <div className="seditor-breakdown-group seditor-breakdown-group--good">
                <div className="seditor-breakdown-h">{tx("Good call — you cut these")}</div>
                {cutRisky.map((id) => {
                  const it = itemsById(id);
                  if (!it) return null;
                  return (
                    <div key={id} className="seditor-breakdown-row">
                      {it.url && <img src={it.url} alt={it.alt ?? ""} className="seditor-breakdown-thumb" />}
                      <span className="seditor-breakdown-text">{it.safeNote ?? it.riskNote}</span>
                    </div>
                  );
                })}
              </div>
            )}
            {totalRisky === 0 && <div className="seditor-breakdown-empty">{tx("No risky items in this batch.")}</div>}
          </div>

          <button type="button" className="btn pstory-continue" onClick={onDone}>
            {config.continueLabel ?? tx("Continue")}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="pstory-wrap seditor-wrap" role="dialog" aria-label={tx("Edit the story before posting")}>
      <div className="pstory-card seditor-card">
        <div className="pstory-head">
          <span className="pstory-head-dot" aria-hidden />
          {tx("Edit before you post")}
        </div>
        {config.intro && <div className="seditor-intro">{config.intro}</div>}

        <div className="seditor-photogrid">
          {config.photos.map((p) => (
            <button
              key={p.id}
              type="button"
              className={`seditor-photo ${kept.has(p.id) ? "on" : "off"}`}
              onClick={() => toggle(p.id)}
            >
              <span className="seditor-photo-imgwrap">
                <img src={p.url} alt={p.alt ?? ""} />
              </span>
              <span className="seditor-photo-badge">{kept.has(p.id) ? tx("Posting") : tx("Cut")}</span>
            </button>
          ))}
        </div>

        <div className="seditor-tags">
          {config.hashtags.map((h) => (
            <button
              key={h.id}
              type="button"
              className={`seditor-tag ${kept.has(h.id) ? "on" : "off"}`}
              onClick={() => toggle(h.id)}
            >
              {h.label}
            </button>
          ))}
        </div>

        <button type="button" className="btn pstory-continue seditor-submit" onClick={submit}>
          {config.submitLabel ?? tx("Post the story")}
        </button>
      </div>
    </div>
  );
}
